<template>
  <v-row no-gutters>
    <v-col cols="12">
      <span>Provider Source Type</span>
    </v-col>
    <v-col cols="12">
      <v-select
        id="ddlProviderSourceType"
        v-model="sourceType"
        :items="providerSourceTypes"
        item-title="text"
        item-value="value"
        variant="solo"
        density="compact"
        placeholder="Select Provider Source Type"
        clearable
        clear-icon="mdi-close"
        color="primary"
        @click:clear="clear"
      >
        <template #item="data">
          <v-list-item
            v-bind="data.props"
            :id="`client-individual-modal--provider-source-type--option-${String(
              data.item.text
            ).replaceAll(' ', '')}`"
          >
            {{ data.item.text }}
          </v-list-item>
        </template>
      </v-select>
    </v-col>
  </v-row>
  <v-row>
    <v-col v-if="displayApi">
      <client-provider-api
        v-model="apiProvider"
        v-model:validInput="validApiInput"
      ></client-provider-api>
    </v-col>
    <v-col v-if="displaySftp">
      <client-provider-sftp v-model="sftpProvider"></client-provider-sftp>
    </v-col>
  </v-row>
  <v-row>
    <v-col cols="12" class="d-flex justify-end align-center">
      <cs-button
        v-if="editMode === 'update'"
        id="btnSaveProvider"
        primary
        label="Save Provider"
        :disabled="saveButtonDisabled"
        :loading="saveActionInProgress"
        @click="showConfirmSaveDialog = true"
      ></cs-button>
    </v-col>
  </v-row>
  <cs-form-dialog
    id="dlg-client-provider-confirm-save"
    v-model="showConfirmSaveDialog"
    body-id="dlg-client-provider-confirm-save-body"
    heading="Client Provider Editor"
    :primary-action="{
      label: 'Continue',
      eventName: 'primary-click',
      loading: saveActionInProgress
    }"
    :secondary-action1="{ label: 'Cancel', eventName: 'secondary1-click' }"
    @primary-click="onSave()"
    @secondary1-click="showConfirmSaveDialog = false"
  >
    <template #cs-form-dialog-content>
      You are about to save the provider settings. Are you sure you want to proceed?
    </template>
  </cs-form-dialog>
  <cs-form-dialog
    id="dlg-client-provider-save-result"
    v-model="showNoticeDialog"
    body-id="dlg-client-provider-save-result-body"
    heading="Client Provider Editor"
    :primary-action="{
      label: 'OK',
      eventName: 'primary-click'
    }"
    @primary-click="onDialogOkBtn"
  >
    <template #cs-form-dialog-content>
      <div v-safe-html="resultMessage"></div>
      <v-spacer></v-spacer>
      <div v-safe-html="warningMessage" class="warning-message text-red-darken-2"></div>
    </template>
  </cs-form-dialog>
</template>
<script>
import { mapGetters } from 'vuex';
import { CSBase } from '@complispace/cs-design-system';
import providerSourceType from '../lib/const/providerSourceType';
import ClientProviderSftp from './ClientProviderSftp';
import ClientProviderApi from './ClientProviderApi';
import * as types from '@/store/mutationTypes';

export default {
  name: 'ClientProvider',

  components: {
    ClientProviderSftp,
    ClientProviderApi
  },

  extends: CSBase,
  props: {
    clientSid: { type: String, required: false, default: '' }
  },

  emits: ['clientProviderChanged', 'clientProviderSaved'],

  data() {
    return {
      editMode: { type: String, required: false, default: 'create' },

      providerSourceTypes: [
        {
          text: providerSourceType.SFTP,
          value: providerSourceType.SFTP
        },
        {
          text: providerSourceType.API,
          value: providerSourceType.API
        }
      ],
      sourceType: null,
      originalSourceType: null,
      apiProvider: { apiKey: '', baseUrl: '', reportSid: '', tenantSid: '' },
      sftpProvider: { sftpPath: '' },
      saveActionInProgress: false,
      showNoticeDialog: false,
      resultMessage: '',
      warningMessage: '',
      showConfirmSaveDialog: false,
      validApiInput: false
    };
  },

  computed: {
    ...mapGetters({
      clientProviderView: 'clients/clientProviderView'
    }),
    displaySftp() {
      return providerSourceType.SFTP === this.sourceType;
    },
    displayApi() {
      return providerSourceType.API === this.sourceType;
    },
    saveButtonDisabled() {
      if (this.originalSourceType !== this.sourceType && this.sourceType === null) {
        return false;
      }
      if (this.sourceType === providerSourceType.API) {
        if (!this.validApiInput) {
          return true;
        }
      }
      return !this.sourceType || this.saveActionInProgress;
    }
  },

  watch: {
    clientProviderView() {
      this.reloadData();
    },
    sourceType() {
      this.updateProvider();
    },
    sftpProvider: {
      handler() {
        this.updateProvider();
      },
      deep: true
    },
    apiProvider: {
      handler() {
        this.updateProvider();
      },
      deep: true
    }
  },

  async mounted() {
    await this.initPage(this.clientSid);
    this.originalSourceType = this.sourceType;
  },

  methods: {
    async initPage(clientSid) {
      if (clientSid !== '') {
        this.editMode = 'update';
        await this.fetchClientProvider();
        this.reloadData();
      }
    },
    async fetchClientProvider() {
      try {
        if (this.clientSid) {
          this.setLoading(true);
          await this.$store.dispatch('clients/fetchClientProviderConfig', this.clientSid);
          this.clearLoading();
        }
      } catch (e) {
        this.clearLoading();
        this.clear();
      }
    },
    async reloadData() {
      const newValue = this.clientProviderView || {};
      this.clear();
      this.sourceType = newValue.sourceType || null;
      if (providerSourceType.SFTP === this.sourceType) {
        this.sftpProvider = newValue.clientSftpProvider || {};
      } else if (providerSourceType.API === this.sourceType) {
        this.apiProvider = newValue.clientApiProvider || {};
      }
    },
    clear() {
      this.apiProvider = { apiKey: '', baseUrl: '', reportSid: '', tenantSid: '' };
      this.sftpProvider = { sftpPath: '' };
    },
    updateProvider() {
      const newConfig = { clientSid: this.clientSid, sourceType: this.sourceType };
      if (providerSourceType.SFTP === this.sourceType) {
        newConfig.clientSftpProvider = this.sftpProvider;
      } else if (providerSourceType.API === this.sourceType) {
        newConfig.clientApiProvider = this.apiProvider;
      }
      this.$emit('clientProviderChanged', newConfig);
    },
    async saveClientProvider() {
      const sourceType = this.sourceType === null ? '' : this.sourceType;
      const newConfig = { clientSid: this.clientSid, sourceType };
      if (this.sourceType === providerSourceType.API) {
        newConfig.clientApiProvider = this.apiProvider;
      } else if (this.sourceType === providerSourceType.SFTP) {
        newConfig.clientSftpProvider = this.sftpProvider;
      }
      const patchRes = await this.$store.dispatch('clients/patchClientProviderConfig', newConfig);

      return patchRes;
    },
    async onSave() {
      this.saveActionInProgress = true;
      try {
        this.$store.commit('app/setSilentAxiosError', true);
        const saveRes = await this.saveClientProvider();
        if (!saveRes || saveRes.status !== 'success') {
          this.saveActionInProgress = false;
          this.handleSaveError(saveRes.error);
          return;
        }
        this.alertSaveResult('success');
        this.$emit('clientProviderSaved', true);
      } catch (e) {
        this.handleSaveError(e);
      } finally {
        this.saveActionInProgress = false;
      }
    },

    alertSaveResult(status, errorMessage, warningMessage) {
      this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      this.warningMessage = '';
      if (status === 'success') {
        this.resultMessage = 'Client Provider Setting has been saved.';
        this.warningMessage = warningMessage || '';
      } else {
        this.resultMessage = errorMessage || `Failed to save Client information.`;
        this.warningMessage = warningMessage || '';
      }
      this.showConfirmSaveDialog = false;
      this.showNoticeDialog = true;
    },

    handleSaveError(error) {
      let displayMessage = error.message || '';
      let warningMessage = '';
      try {
        const errorMessages = error.message?.split(':') || [];
        if (errorMessages.length > 3) {
          // eslint-disable-next-line prefer-destructuring
          displayMessage = `Save Error: ${errorMessages[2]}`;
          warningMessage = `errorRefId: ${errorMessages[1]}`;
        }
        this.alertSaveResult(
          'error',
          `Failed to save Client information. <br/> ${displayMessage}`,
          warningMessage
        );
      } finally {
        this.$store.commit('app/setSilentAxiosError', true);
      }
    },

    onDialogOkBtn() {
      this.showNoticeDialog = false;
      this.warningMessage = '';
      this.resultMessage = '';
    }
  }
};
</script>
<style scoped></style>
